"use client";

import { useCallback, useState } from "react";
import { api } from "@/lib/api-client";
import { getUserFacingError } from "@/lib/api-errors";
import type { CreateOrderPayload, VerifyPaymentResponse } from "@/lib/api-types";
import { loadRazorpayScript } from "@/lib/razorpay";
import { simulatePaymentFailed } from "@/lib/simulate-payment-webhook";
import { site } from "@/content/site";

export interface CheckoutResult {
  success: boolean;
  cancelled?: boolean;
  incomplete?: boolean;
  donationId?: string;
  downloadToken?: string;
  successToken?: string;
  receiptDownloadUrl?: string;
  certificateDownloadUrl?: string;
}

export type CheckoutOptions = {
  /** Dev/mock only: `fail` sends payment.failed webhook; `cancel` aborts checkout. */
  paymentTest?: "fail" | "cancel";
};

function toCheckoutResult(result: VerifyPaymentResponse): CheckoutResult {
  return {
    success: result.success,
    donationId: result.donationId,
    downloadToken: result.downloadToken,
    successToken: result.successToken,
    receiptDownloadUrl: result.receiptDownloadUrl,
    certificateDownloadUrl: result.certificateDownloadUrl,
  };
}

function finalizeCheckoutResult(result: CheckoutResult): CheckoutResult {
  if (result.success && result.donationId) {
    if (
      result.successToken ||
      result.downloadToken ||
      result.receiptDownloadUrl ||
      result.certificateDownloadUrl
    ) {
      return result;
    }
    return { ...result, incomplete: true };
  }
  return result;
}

export function useRazorpayCheckout() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkout = useCallback(
    async (
      payload: CreateOrderPayload,
      options?: CheckoutOptions
    ): Promise<CheckoutResult> => {
      setIsProcessing(true);
      setError(null);

      try {
        const order = await api.createDonationOrder(payload);
        const useMock =
          process.env.NEXT_PUBLIC_USE_MOCK_API === "true" ||
          order.razorpayKeyId === "rzp_test_mock_key";

        if (useMock) {
          if (options?.paymentTest === "cancel") {
            return { success: false, cancelled: true };
          }
          if (options?.paymentTest === "fail") {
            await simulatePaymentFailed(order.orderId);
            return { success: false };
          }

          const verifyResult = await api.verifyDonation({
            razorpay_order_id: order.orderId,
            razorpay_payment_id: `pay_mock_${Date.now()}`,
            razorpay_signature: "mock_signature",
          });
          const mockResult = finalizeCheckoutResult(toCheckoutResult(verifyResult));
          if (mockResult.incomplete) {
            setError(
              "Payment succeeded but confirmation is incomplete. Please contact us with your reference."
            );
          }
          return mockResult;
        }

        const loaded = await loadRazorpayScript();
        if (!loaded) {
          throw new Error("Failed to load payment gateway. Please try again.");
        }

        const result = await new Promise<CheckoutResult>((resolve) => {
          const rzp = new window.Razorpay({
            key: order.razorpayKeyId,
            amount: order.amount,
            currency: order.currency,
            order_id: order.orderId,
            name: site.shortName,
            description: `Donation - ${payload.category}`,
            prefill: {
              name: `${payload.donor.firstName} ${payload.donor.lastName}`,
              email: payload.donor.email,
              contact: payload.donor.phone,
            },
            handler: async (response) => {
              try {
                const verifyResult = await api.verifyDonation(response);
                const checkoutResult = finalizeCheckoutResult(
                  toCheckoutResult(verifyResult)
                );
                if (checkoutResult.incomplete) {
                  setError(
                    "Payment succeeded but confirmation is incomplete. Please contact us with your reference."
                  );
                }
                resolve(checkoutResult);
              } catch (e) {
                const message = getUserFacingError(e);
                setError(message);
                resolve({ success: false });
              }
            },
            modal: {
              ondismiss: () => {
                resolve({ success: false, cancelled: true });
              },
            },
          });
          rzp.open();
        });

        return result;
      } catch (e) {
        const message = getUserFacingError(e);
        setError(message);
        return { success: false };
      } finally {
        setIsProcessing(false);
      }
    },
    []
  );

  return { checkout, isProcessing, error, clearError: () => setError(null) };
}
